/**
 * FieldFiller — the v2 ceremony's guided field-completion experience (CB-5.6, FR-51 / FR-6).
 *
 * Renders the signer's OWN assigned fields as an overlay over the CB-5.4 document (positioned only
 * via the shared `contractCoords` util — enforcement rule 5), drives a persistent "Next field →"
 * with a "X of N required complete" progress count, and GATES Finish until every required field is
 * complete. Date-Signed fields auto-fill at signing and are locked (FR-6). Every value persists
 * through the token-scoped `rpc_fill_field` RPC (own-fields-only; never sibling signers').
 *
 * Accessibility (NFR-14 / UX-DR27): a blocked Finish highlights offenders, announces a named-list
 * summary via an aria-live region, and moves focus to the first offender; each required-unfilled
 * field carries outline + text message + aria-invalid + aria-describedby (never color-only). Finish
 * is kept focusable (aria-disabled, not bare `disabled`) so AT users can discover WHY it's blocked.
 *
 * Scope: this story enables/blocks Finish and owns the completeness contract. It does NOT advance the
 * envelope or write `completed` (seal worker only) — `onFinish` is the seam the completion/submit
 * story (CB-5.10) wires to the real signer-submit.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, PenLine, AlertCircle, Loader2 } from 'lucide-react';
import { contractService } from '@/services/contractService';
import type { SigningFieldV2 } from '@/types/contracts';
import { type SignatureValue, fontForStyle, hasSignature } from '@/components/settings/contracts/storedSignature';
import { accentTextOnLight } from '@/lib/tenantAccent';
import { denormalizeToPixels } from '@/lib/contractCoords';
import CeremonyDocumentViewer from './CeremonyDocumentViewer';
import { signatureFieldValue, dateSignedValue, todayUtcDate, isEmptyValue } from './fieldFill';

interface FieldFillerProps {
  token: string;
  /** The signature adopted in CB-5.5 (applied to signature/initials fields). */
  adoptedSignature: SignatureValue | null;
  accent: string;
  ink: string;
  /** The real signer-submit (CB-5.10): performs the Finish POST. Resolves `{ ok: true }` on success
   *  (the shell then swaps to the completion screen) or `{ ok: false, error }` on failure so
   *  FieldFiller can surface the SERVER's message (e.g. "expired") rather than a generic retry. */
  onFinish?: () => Promise<{ ok: boolean; error?: string }>;
}

const SIGNATURE_TYPES = new Set(['signature', 'initials']);

function fieldLabel(f: SigningFieldV2): string {
  switch (f.field_type) {
    case 'signature':
      return 'Signature';
    case 'initials':
      return 'Initials';
    case 'date_signed':
      return 'Date signed';
    case 'text':
      return 'Text';
    case 'checkbox':
      return 'Checkbox';
    default:
      return 'Field';
  }
}

export default function FieldFiller({ token, adoptedSignature, accent, ink, onFinish }: FieldFillerProps) {
  const [fields, setFields] = useState<SigningFieldV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Local filled state: fieldId → value JSONB (seeded from loaded values, updated on each write).
  const [values, setValues] = useState<Record<string, Record<string, unknown> | null>>({});
  const [filledAt, setFilledAt] = useState<Record<string, string | null>>({});
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());
  const [announce, setAnnounce] = useState('');
  // CB-5.10: the real submit is async — track in-flight + a recoverable submit error.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const chipRefs = useRef<Record<string, HTMLElement | null>>({});
  const dateSignedAutofilledRef = useRef(false);
  // Accent made safe as text/icon on a light background (the filled chips sit on white) — UX-DR3 for
  // the secondary accent-colored elements, since the raw tenant accent can fail 4.5:1 on white.
  const accentText = accentTextOnLight(accent);

  // ── Load the signer's own fields ──
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      const r = await contractService.getSigningFieldsV2(token);
      if (cancelled) return;
      if (!r.success || !r.data) {
        setLoadError(r.error || "We couldn't load your fields.");
        setLoading(false);
        return;
      }
      setFields(r.data);
      // Rehydrate prior values (resume / sender pre-fill) so progress + gating reflect them.
      const seedVals: Record<string, Record<string, unknown> | null> = {};
      const seedAt: Record<string, string | null> = {};
      for (const f of r.data) {
        seedVals[f.id] = f.value ?? null;
        seedAt[f.id] = f.filled_at;
      }
      setValues(seedVals);
      setFilledAt(seedAt);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const isComplete = useCallback(
    // Linked fields are derived (mirror their source) — always considered complete here so they
    // never gate Finish, mirroring the server (which excludes them from the required check).
    (f: SigningFieldV2) => Boolean(f.value?.linked_to) || (Boolean(filledAt[f.id]) && !isEmptyValue(values[f.id])),
    [filledAt, values],
  );

  const requiredFields = useMemo(() => fields.filter((f) => f.required), [fields]);
  const completedRequired = useMemo(
    () => requiredFields.filter(isComplete).length,
    [requiredFields, isComplete],
  );
  const totalRequired = requiredFields.length;
  const allComplete = totalRequired > 0 ? completedRequired === totalRequired : fields.every(isComplete);

  // ── Persist one field; update local state on success ──
  const writeField = useCallback(
    async (field: SigningFieldV2, value: Record<string, unknown>) => {
      const r = await contractService.fillFieldV2(token, field.id, value);
      if (!r.success) {
        setAnnounce(r.error || 'We couldn\'t save that field.');
        return false;
      }
      setValues((p) => ({ ...p, [field.id]: value }));
      setFilledAt((p) => ({ ...p, [field.id]: r.data?.filled_at ?? todayUtcDate() }));
      setErrorIds((p) => {
        if (!p.has(field.id)) return p;
        const next = new Set(p);
        next.delete(field.id);
        return next;
      });
      return true;
    },
    [token],
  );

  // ── Date-Signed auto-fill at signing (FR-6): the signer reached this step with an adopted
  //    signature, so the signing moment has arrived — fill any unfilled date_signed fields once. ──
  useEffect(() => {
    if (loading || dateSignedAutofilledRef.current || !adoptedSignature) return;
    const pending = fields.filter((f) => f.field_type === 'date_signed' && !filledAt[f.id]);
    if (pending.length === 0) return;
    dateSignedAutofilledRef.current = true; // claim before the loop to prevent mid-loop re-entry
    (async () => {
      let allOk = true;
      for (const f of pending) {
        const ok = await writeField(f, dateSignedValue(todayUtcDate()));
        if (!ok) allOk = false;
      }
      // Release the guard on failure so a transient error doesn't permanently leave date_signed
      // unfilled (Finish blocked) — a later re-render re-attempts the still-pending fields.
      if (!allOk) dateSignedAutofilledRef.current = false;
    })();
  }, [loading, adoptedSignature, fields, filledAt, writeField]);

  // ── Apply the adopted signature to one signature/initials field ──
  //    Guard on hasSignature (not just non-null): a degenerate adopted value would otherwise persist an
  //    empty signature that looks signed but counts incomplete.
  const applySignature = useCallback(
    (field: SigningFieldV2) => {
      if (!adoptedSignature || !hasSignature(adoptedSignature)) return;
      void writeField(field, signatureFieldValue(adoptedSignature));
    },
    [adoptedSignature, writeField],
  );

  // ── "Apply to all my fields": fill every signature/initials field this signer owns at once ──
  const signatureFields = useMemo(
    () => fields.filter((f) => SIGNATURE_TYPES.has(f.field_type)),
    [fields],
  );
  const applyToAll = useCallback(async () => {
    if (!adoptedSignature || !hasSignature(adoptedSignature)) return;
    // Surface partial failures (a mid-loop fillFieldV2 error) instead of silently leaving some fields
    // unsigned — writeField already announces the per-field error; here we summarize what didn't apply.
    let failed = 0;
    for (const f of signatureFields) {
      if (!isComplete(f)) {
        const ok = await writeField(f, signatureFieldValue(adoptedSignature));
        if (!ok) failed += 1;
      }
    }
    if (failed > 0) {
      setAnnounce(`${failed} field${failed === 1 ? '' : 's'} couldn't be signed. Please try again.`);
    }
  }, [adoptedSignature, signatureFields, isComplete, writeField]);

  // ── Next field: focus the next incomplete required field (or Finish when done) ──
  const focusField = useCallback((id: string) => {
    const el = chipRefs.current[id];
    if (!el) return;
    // NFR-14/UX-DR25: honor prefers-reduced-motion — the focus JUMP still happens, but the scroll is
    // instant rather than animated for users who've asked for reduced motion.
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    el.focus({ preventScroll: true });
  }, []);

  const nextIncompleteRequired = useCallback(
    () => requiredFields.find((f) => !isComplete(f)) || null,
    [requiredFields, isComplete],
  );

  const finishRef = useRef<HTMLButtonElement | null>(null);
  const handleNext = useCallback(() => {
    const next = nextIncompleteRequired();
    if (next) {
      focusField(next.id);
    } else {
      finishRef.current?.focus();
      setAnnounce('All required fields complete. You can finish signing.');
    }
  }, [nextIncompleteRequired, focusField]);

  // ── CB-5.9 resume landing: a returning signer lands on the first unfilled required field (or
  //    Finish if none remain), so they pick up exactly where they left off (FR-54 / UX-DR21). Gated
  //    to a genuine resume — entered with ≥1 pre-filled value — so a fresh ceremony never yanks
  //    focus/scroll. Chips mount only after the document viewer finishes its async page render, so we
  //    poll briefly (bounded) for the target chip to exist, then focus it once. ──
  const resumeLandedRef = useRef(false);
  useEffect(() => {
    if (loading || resumeLandedRef.current) return;
    resumeLandedRef.current = true;
    const isResume = fields.some((f) => Boolean(filledAt[f.id]));
    if (!isResume) return;
    const target = requiredFields.find((f) => !isComplete(f)) ?? null;
    if (!target) {
      finishRef.current?.focus();
      return;
    }
    let attempts = 0;
    const MAX_ATTEMPTS = 34; // ~5s at 150ms — bounded so it never spins past the doc render
    const timer = window.setInterval(() => {
      attempts += 1;
      const el = chipRefs.current[target.id];
      if (el) {
        window.clearInterval(timer);
        focusField(target.id);
      } else if (attempts >= MAX_ATTEMPTS) {
        window.clearInterval(timer);
      }
    }, 150);
    return () => window.clearInterval(timer);
    // requiredFields/isComplete are read once at landing time; resumeLandedRef makes this fire once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // ── Finish: blocked → highlight + announce + focus first offender; complete → the REAL submit
  //    (CB-5.10), with an in-flight lock + a recoverable error if it fails. On success the shell
  //    unmounts this component to show the completion screen. ──
  const handleFinish = useCallback(async () => {
    if (submitting) return;
    const missing = requiredFields.filter((f) => !isComplete(f));
    if (missing.length > 0) {
      setErrorIds(new Set(missing.map((f) => f.id)));
      const names = missing.map(fieldLabel).join(', ');
      setAnnounce(`${missing.length} required field${missing.length === 1 ? '' : 's'} remaining: ${names}`);
      focusField(missing[0].id);
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    setAnnounce('Finishing your signature…');
    // Wrap the submit so a thrown/rejected onFinish can't leave the button stuck in its in-flight
    // state; surface the SERVER's message (e.g. "this contract has expired") rather than a generic one.
    let result: { ok: boolean; error?: string };
    try {
      result = (await onFinish?.()) ?? { ok: false };
    } catch {
      result = { ok: false };
    }
    if (!result.ok) {
      // Failure (network / server gate): unlock + surface the reason; success leaves the lock on while
      // the shell swaps in the completion screen.
      setSubmitting(false);
      const msg = result.error || "We couldn't finish signing. Please check your connection and try again.";
      setSubmitError(msg);
      setAnnounce(msg);
    }
  }, [submitting, requiredFields, isComplete, onFinish, focusField]);

  if (loading) {
    return (
      <div className="mt-6 flex justify-center py-10" aria-busy="true">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200"
          style={{ borderTopColor: accent }}
          role="status"
          aria-label="Loading your fields"
        />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-600" role="status">
        {loadError}
      </div>
    );
  }

  // ── A single field chip, absolutely positioned within its page box ──
  const renderChip = (field: SigningFieldV2, box: { left: number; top: number; width: number; height: number }) => {
    const complete = isComplete(field);
    const hasError = errorIds.has(field.id);
    const msgId = `field-err-${field.id}`;
    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      left: box.left,
      top: box.top,
      width: box.width,
      height: box.height,
    };
    const errorRing = hasError ? '0 0 0 2px #dc2626' : complete ? `0 0 0 1px ${accent}` : `0 0 0 1px ${accent}80`;
    const val = values[field.id];

    // Linked field (named + linked): read-only, mirrors the SOURCE field's LIVE value — fill the
    // source once and this updates automatically. Not editable, not in the fill/required flow.
    const linkedTo = typeof field.value?.linked_to === 'string' ? (field.value.linked_to as string) : null;
    if (linkedTo) {
      const source = fields.find((f) => f.id === linkedTo);
      const srcVal = values[linkedTo];
      let display = '';
      if (srcVal) {
        display = source?.field_type === 'checkbox'
          ? (srcVal.value ? '✓' : '')
          : String((srcVal.typed as string) ?? (srcVal.value as string) ?? '');
      }
      return (
        <div
          key={field.id}
          ref={(el) => { chipRefs.current[field.id] = el; }}
          style={{ ...baseStyle, boxShadow: `0 0 0 1px ${accent}80` }}
          className="flex items-center overflow-hidden rounded-[3px] bg-white/85 px-1 text-[12px] text-slate-700"
          aria-label={`${fieldLabel(field)} — copied automatically from ${source ? fieldLabel(source) : 'another field'}`}
        >
          {display || <span className="text-[10px] italic text-slate-400">Auto-filled</span>}
        </div>
      );
    }

    // Date-Signed: locked, not editable, not in the manual tab order.
    if (field.field_type === 'date_signed') {
      return (
        <div
          key={field.id}
          ref={(el) => { chipRefs.current[field.id] = el; }}
          style={{ ...baseStyle, boxShadow: complete ? `0 0 0 1px ${accent}` : `0 0 0 1px ${accent}80` }}
          className="flex items-center justify-center overflow-hidden rounded-[3px] bg-white/85 px-1 text-[11px] text-slate-700"
          aria-label={`Date signed, auto-filled${complete ? '' : ', pending'}`}
        >
          {complete ? String(val?.value ?? '') : 'Date'}
        </div>
      );
    }

    // Text: inline CONTROLLED input — `value` from state so a resumed value shows (uncontrolled
    // defaultValue is read once at mount, before the async field load, so it rendered empty on resume)
    // and so clearing it persists the empty value (the old blur-only `if (text)` left stale data that
    // still counted complete).
    if (field.field_type === 'text') {
      return (
        <input
          key={field.id}
          ref={(el) => { chipRefs.current[field.id] = el; }}
          type="text"
          value={typeof val?.value === 'string' ? val.value : ''}
          onChange={(e) => setValues((p) => ({ ...p, [field.id]: { value: e.target.value } }))}
          onBlur={(e) => void writeField(field, { value: e.target.value.trim() })}
          aria-label={`${fieldLabel(field)}${field.required ? ', required' : ''}`}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? msgId : undefined}
          style={{ ...baseStyle, boxShadow: errorRing }}
          className="overflow-hidden rounded-[3px] bg-white/90 px-1 text-[12px] text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        />
      );
    }

    // Checkbox.
    if (field.field_type === 'checkbox') {
      const checked = Boolean(val?.value);
      return (
        <button
          key={field.id}
          ref={(el) => { chipRefs.current[field.id] = el; }}
          type="button"
          role="checkbox"
          aria-checked={checked}
          aria-label={`${fieldLabel(field)}${field.required ? ', required' : ''}`}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? msgId : undefined}
          onClick={() => void writeField(field, { value: !checked })}
          style={{ ...baseStyle, boxShadow: errorRing }}
          className="flex items-center justify-center rounded-[3px] bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        >
          {checked ? <Check className="h-4 w-4" style={{ color: accentText }} aria-hidden="true" /> : null}
        </button>
      );
    }

    // Signature / initials: tap to apply the adopted signature.
    if (field.field_type === 'signature' || field.field_type === 'initials') {
      return (
        <button
          key={field.id}
          ref={(el) => { chipRefs.current[field.id] = el; }}
          type="button"
          onClick={() => applySignature(field)}
          aria-label={`${fieldLabel(field)}${field.required ? ', required' : ''}${complete ? ', signed' : ', tap to apply your signature'}`}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? msgId : undefined}
          style={{ ...baseStyle, boxShadow: errorRing }}
          className="flex items-center justify-center overflow-hidden rounded-[3px] bg-white/85 px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        >
          {complete && val ? (
            val.kind === 'image' && typeof val.image === 'string' ? (
              <img src={val.image} alt="" className="max-h-full max-w-full object-contain" />
            ) : (
              <span
                className="truncate text-slate-900"
                style={{ fontFamily: fontForStyle(typeof val.style === 'string' ? val.style : null), fontSize: Math.min(box.height * 0.7, 22) }}
              >
                {String(val.typed ?? val.value ?? '')}
              </span>
            )
          ) : (
            <span className="flex items-center gap-1 text-[10px] font-medium" style={{ color: accentText }}>
              <PenLine className="h-3 w-3" aria-hidden="true" /> Sign
            </span>
          )}
        </button>
      );
    }

    // Unknown/unsupported field_type: render an inert, read-only marker rather than mis-treating it as
    // a signature slot (tapping a fallthrough button would write a signature value into a non-signature
    // field). Surfaces the field's presence without an action this client doesn't understand.
    return (
      <div
        key={field.id}
        ref={(el) => { chipRefs.current[field.id] = el; }}
        style={{ ...baseStyle, boxShadow: `0 0 0 1px ${accent}80` }}
        className="flex items-center justify-center overflow-hidden rounded-[3px] bg-slate-100/80 px-1 text-[10px] text-slate-400"
        aria-label={`${fieldLabel(field)} (not editable here)`}
      />
    );
  };

  const hasMultipleSignatureSlots = signatureFields.length > 1 && Boolean(adoptedSignature);

  return (
    <section className="mt-6" aria-label="Complete and sign">
      {/* aria-live summary for blocked-Finish + status (assertive so AT announces misses reliably). */}
      <div role="status" aria-live="assertive" className="sr-only">
        {announce}
      </div>

      {/* Visible text messages for offending fields (referenced by each chip's aria-describedby). */}
      {errorIds.size > 0 ? (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3">
          <p className="flex items-center gap-1.5 text-sm font-medium text-red-700">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            {errorIds.size} required field{errorIds.size === 1 ? '' : 's'} still need attention:
          </p>
          <ul className="mt-1 space-y-0.5">
            {fields
              .filter((f) => errorIds.has(f.id))
              .map((f) => (
                <li key={f.id} id={`field-err-${f.id}`} className="text-sm text-red-700">
                  {fieldLabel(f)} required
                </li>
              ))}
          </ul>
        </div>
      ) : null}

      <CeremonyDocumentViewer
        token={token}
        accent={accent}
        renderPageOverlay={(pageIndex, wPx, hPx) => (
          <>
            {fields
              .filter((f) => f.page_index === pageIndex)
              .map((f) =>
                renderChip(f, denormalizeToPixels({ page_index: f.page_index, x: f.x, y: f.y, w: f.w, h: f.h }, wPx, hPx)),
              )}
          </>
        )}
      />

      {hasMultipleSignatureSlots ? (
        <button
          type="button"
          onClick={() => void applyToAll()}
          className="mt-3 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border px-4 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{ borderColor: accent, color: accentText }}
        >
          <PenLine className="h-4 w-4" aria-hidden="true" />
          Apply my signature to all my fields
        </button>
      ) : null}

      {submitError ? (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
          {submitError}
        </p>
      ) : null}

      {/* Sticky action bar (CB-5.1 placeholder → real): Next field + progress + Finish. */}
      <div className="sticky bottom-0 left-0 right-0 mt-4 border-t border-slate-200 bg-white/95 py-3 backdrop-blur">
        <div className="mb-2 flex items-center justify-between text-sm text-slate-600">
          <span aria-live="polite">
            {totalRequired > 0 ? `${completedRequired} of ${totalRequired} required complete` : 'No required fields'}
          </span>
        </div>
        <div
          className="mb-3 h-2 w-full overflow-hidden rounded-full bg-slate-200"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={totalRequired || 1}
          aria-valuenow={completedRequired}
        >
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: totalRequired > 0 ? `${(completedRequired / totalRequired) * 100}%` : '100%',
              backgroundColor: accent,
            }}
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleNext}
            className="flex min-h-[44px] flex-1 items-center justify-center rounded-xl border border-slate-300 px-4 text-base font-semibold text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
          >
            Next field →
          </button>
          <button
            ref={finishRef}
            type="button"
            onClick={handleFinish}
            aria-disabled={!allComplete || submitting}
            aria-busy={submitting}
            className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl px-4 text-base font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{ backgroundColor: accent, color: ink, opacity: allComplete && !submitting ? 1 : 0.55 }}
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : null}
            {submitting ? 'Finishing…' : 'Finish'}
          </button>
        </div>
      </div>
    </section>
  );
}
